import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { getStripeKey } from '#config/stripe';

import { useLocale } from '#src/app/shared/hooks/l10n';

import { toStripeLocale } from '../utils/toStripeLocale';

export const StripeLoader: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	const utilityContext = useUtilityContext();

	// Stripe is a third-party API, so we resolve against the *api* environment
	// (which honours dev/staging proxy targets). For personalized demo hosts
	// (e.g. `jlou.changeeng.org` -> cyan) we prefer the resolved target so the
	// publishable key matches the back-end's configured Stripe account; we fall
	// back to the raw sub-environment so canonical demo hosts (jade, cyan, ...)
	// still resolve correctly.
	const environment = utilityContext.environment.getApiEnvironment();
	const subEnvironment =
		utilityContext.environment.getApiDemoEnvironmentTarget() ?? utilityContext.environment.getApiSubEnvironment();
	const stripeKey = getStripeKey({ environment, subEnvironment });
	const locale = useLocale();
	// we actually want a promise here
	// eslint-disable-next-line react-hooks/use-memo
	const stripePromise = useMemo(async () => loadStripe(stripeKey), [stripeKey]);

	return (
		// locale is immutable after mount — runtime locale changes won't re-translate the iframe,
		// so we key on it to force a remount when the app locale changes.
		<Elements key={locale} stripe={stripePromise} options={{ locale: toStripeLocale(locale) }}>
			{children}
		</Elements>
	);
};
